import { AppBar, Toolbar, Box } from '@mui/material';

import { BannerAlertMessagesCollection, BannerMobileAlertMessagesCollection, Header } from '@/graphql/__generated/sdk';
import { Maybe } from '@/types';
import ImageWrapper from '@/components/ImageWrapper';
import TAGLink from '@/components/TAGLink';
import Banner from '@/components/Banner/Banner';

import NavigationBar from '../NavigationBar/NavigationBar';
import { IHeaderWithLocationNav } from '../HeaderWithLocationNav';
import LocationNavBar from '../LocationNavBar/LocationNavBar';

import {
	logoStyles,
	headerStyles,
	navigationStyles,
	navigationLinkStyles,
	toolbarStyles,
	toolbarWrapStyles,
} from './HeaderDesktop.styles';

export function bannerConfigExtractor(
	headerData?: Maybe<Header>,
	isMobile?: boolean
): {
	topAlertBannerMessages: BannerAlertMessagesCollection | BannerMobileAlertMessagesCollection;
	bottomAlertBannerMessages: BannerAlertMessagesCollection | BannerMobileAlertMessagesCollection;
	showTopAlertBannerMessages: boolean;
	showBottomAlertBannerMessages: boolean;
} {
	let topAlertBannerMessages;
	let bottomAlertBannerMessages;

	const { topAlertBanner, bottomAlertBanner } = headerData ?? {};

	if (isMobile) {
		topAlertBannerMessages =
			topAlertBanner?.mobileAlertMessagesCollection ?? ({} as BannerMobileAlertMessagesCollection);
		bottomAlertBannerMessages =
			bottomAlertBanner?.mobileAlertMessagesCollection ?? ({} as BannerMobileAlertMessagesCollection);
	} else {
		topAlertBannerMessages = topAlertBanner?.alertMessagesCollection ?? ({} as BannerAlertMessagesCollection);
		bottomAlertBannerMessages = bottomAlertBanner?.alertMessagesCollection ?? ({} as BannerAlertMessagesCollection);
	}

	const showTopAlertBannerMessages = topAlertBannerMessages && topAlertBannerMessages.items?.length > 0;
	const showBottomAlertBannerMessages = bottomAlertBannerMessages && bottomAlertBannerMessages.items?.length > 0;

	return {
		topAlertBannerMessages,
		bottomAlertBannerMessages,
		showTopAlertBannerMessages,
		showBottomAlertBannerMessages,
	};
}

export default function HeaderDesktop({ logoUrl, logoHref = '/', headerData }: Readonly<IHeaderWithLocationNav>) {
	const {
		topAlertBannerMessages,
		bottomAlertBannerMessages,
		showTopAlertBannerMessages,
		showBottomAlertBannerMessages,
	} = bannerConfigExtractor(headerData);

	return (
		<AppBar data-testid="header_desktop_with_location_nav" sx={headerStyles}>
			{showTopAlertBannerMessages && (
				<Banner
					dataTestId="header_desktop_top_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={topAlertBannerMessages as BannerAlertMessagesCollection}
				/>
			)}
			<Toolbar disableGutters sx={toolbarStyles}>
				<Box sx={toolbarWrapStyles}>
					<TAGLink href={logoHref}>
						<ImageWrapper
							src={logoUrl}
							width="unset"
							height="unset"
							sx={logoStyles}
							data-testid="img_logo_desktop_with_location_nav"
							alt="brand logo"
						/>
					</TAGLink>
					<NavigationBar
						navigationItems={headerData?.navigationLinksCollection}
						navigationSx={navigationStyles}
						navigationLinkSx={navigationLinkStyles}
					/>
					<NavigationBar
						navigationItems={headerData?.secondaryNavigationLinksCollection}
						navigationSx={navigationStyles}
						navigationLinkSx={navigationLinkStyles}
						linkColor="text.interactive"
						fontWeight={700}
						showIconText={true}
					/>
				</Box>
			</Toolbar>
			<LocationNavBar
				schedulingButtonText={headerData?.schedulingButtonText}
				locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
				schedulingButtonUrl={headerData?.schedulingButtonUrl}
				searchFieldTitle={headerData?.searchFieldTitle}
			/>
			{showBottomAlertBannerMessages && (
				<Banner
					dataTestId="header_desktop_bottom_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={bottomAlertBannerMessages as BannerAlertMessagesCollection}
				/>
			)}
		</AppBar>
	);
}
